/**
 * @file App.tsx
 * @description Main XiaVan application component with routing and authentication
 * 
 * Structure:
 * - Authentication system with Google OAuth
 * - Protected routes (require login)
 * - Theme system with CSS variables (light/dark, interchangeable)
 * - React Router for page navigation
 * - Fixed header with navigation
 * - Routes: /login (public), /, /plan/:id, /favoritos (protected)
 * - Footer with theme selector and admin features
 * 
 * Features:
 * - Google OAuth login + email whitelist
 * - Private route protection
 * - Responsive fullwidth design
 * - Shareable URLs for each plan
 * - Deep linking to favorites
 * - Browser back/forward navigation
 * - Interchangeable theme system
 * - Dark/light mode with cookie persistence
 * - Load favorites from API on start
 * - Code splitting with React.lazy for performance optimization
 */

import { useEffect, lazy, Suspense, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import ProtectedRoute from './components/auth/ProtectedRoute'
import AdminRoute from './components/auth/AdminRoute'
import Header from './components/layout/Header'
import { ToastContainer } from './components/ui/Toast'
import { useToastStore } from './hooks/useToast'
import { usePlansStore } from './store/planesStore'
import { useTheme } from './hooks/useTheme'
import InstallPrompt from './components/features/InstallPrompt'
import UpdateNotification from './components/features/UpdateNotification'
import '@/styles/themes.css'

// Lazy load pages for better performance (code splitting)
const LoginPage = lazy(() => import('./pages/LoginPage'))
const HomePage = lazy(() => import('./pages/HomePage'))
const PlanPage = lazy(() => import('./pages/PlanPage'))
const FavoritosPage = lazy(() => import('./pages/FavoritosPage'))
const AdminPage = lazy(() => import('./pages/AdminPage'))
const SeedDataPage = lazy(() => import('./pages/SeedDataPage'))
const OfflinePage = lazy(() => import('./pages/OfflinePage'))

// Lazy load Footer (secondary component, below fold)
const Footer = lazy(() => import('./components/layout/Footer'))

/**
 * Loading fallback component for lazy loaded routes
 */
function LoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white dark:bg-gray-900">
      <div className="flex flex-col items-center gap-4">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-gray-600 dark:text-gray-400">Cargando...</p>
      </div>
    </div>
  )
}

function App() {
  // Initialize theme system (applies cookie values to DOM)
  useTheme()

  // Get toast store
  const toasts = useToastStore((state) => state.toasts)
  const removeToast = useToastStore((state) => state.removeToast)

  // Get action to load favorites from store
  const loadFavorites = usePlansStore((state) => state.loadFavorites)

  // Lazy load Footer with IntersectionObserver (loads when near bottom)
  const [shouldLoadFooter, setShouldLoadFooter] = useState(false)
  const footerTriggerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadFavorites()

    // IntersectionObserver to load Footer when scrolling down
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoadFooter(true)
        }
      },
      { rootMargin: '200px' } // Load 200px before trigger element
    )

    if (footerTriggerRef.current) {
      observer.observe(footerTriggerRef.current)
    }

    return () => observer.disconnect()
  }, [loadFavorites])

  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public route: Login */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Public route: Offline fallback */}
            <Route path="/offline" element={<OfflinePage />} />

            {/* Protected routes: Require authentication */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <div className="flex min-h-screen flex-col bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                    {/* Sticky header with logo and navigation */}
                    <Header />

                    <main className="w-full flex-1 py-8">
                      <Suspense fallback={<LoadingFallback />}>
                        <Routes>
                          <Route path="/" element={<HomePage />} />
                          <Route path="/plan/:id" element={<PlanPage />} />
                          <Route path="/favoritos" element={<FavoritosPage />} />
                          <Route
                            path="/admin"
                            element={
                              <AdminRoute>
                                <AdminPage />
                              </AdminRoute>
                            }
                          />
                          <Route
                            path="/seed-data"
                            element={
                              <AdminRoute>
                                <SeedDataPage />
                              </AdminRoute>
                            }
                          />
                        </Routes>
                      </Suspense>
                    </main>

                    {/* Footer trigger for IntersectionObserver (lazy load) */}
                    <div ref={footerTriggerRef} className="h-1" />
                    
                    {/* Footer with theme selector (lazy loaded when scrolling down) */}
                    {shouldLoadFooter && (
                      <Suspense fallback={<div className="h-40 border-t border-gray-200 dark:border-gray-700" />}>
                        <Footer />
                      </Suspense>
                    )}
                  </div>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>

        {/* Toast notifications (global) */}
        <ToastContainer toasts={toasts} onClose={removeToast} />
        
        {/* PWA features */}
        <InstallPrompt />
        <UpdateNotification />
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
